import React, { useState, FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase/firebase";

/* ===================== SVG Icons ===================== */
type IconProps = { size?: number; color?: string; strokeWidth?: number };

const Svg: React.FC<IconProps & { children: React.ReactNode }> = ({
  size = 24,
  color = "currentColor",
  strokeWidth = 2,
  children,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const IconCart: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </Svg>
);
const IconBolt: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </Svg>
);
const IconPin: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </Svg>
);
const IconCar: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M5 17H3v-5l2.5-6h13L21 12v5h-2" />
    <path d="M3 12h18" />
    <circle cx="7.5" cy="17" r="2" />
    <circle cx="16.5" cy="17" r="2" />
  </Svg>
);
const IconCode: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </Svg>
);
const IconMail: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M22 6l-10 7L2 6" />
  </Svg>
);
const IconLock: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </Svg>
);
const IconEye: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </Svg>
);
const IconEyeOff: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M17.94 17.94A10 10 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9 9 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </Svg>
);
const IconAlert: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </Svg>
);
const IconClose: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </Svg>
);
const IconArrow: React.FC<IconProps> = (p) => (
  <Svg {...p}>
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </Svg>
);
const IconSpinner: React.FC<IconProps> = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    style={{ animation: "spin 0.8s linear infinite" }}
  >
    <circle
      cx="12"
      cy="12"
      r="9"
      fill="none"
      stroke="rgba(255,255,255,0.35)"
      strokeWidth="3"
    />
    <path
      d="M21 12a9 9 0 0 0-9-9"
      fill="none"
      stroke="#fff"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

/* ===================== Landing Page ===================== */
const LandingPage: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div style={styles.page}>
      {/* ===== Header ===== */}
      <header style={styles.header}>
        <div style={styles.brand}>
          <div style={styles.brandLogo}>
            <img
              src="/logoZapee.jpeg"
              alt="Zetuka Tech"
              style={styles.brandLogoImg}
            />
          </div>
          <div>
            <div style={styles.brandName}>ZETUKA TECH</div>
            <div style={styles.brandSub}>Private Limited</div>
          </div>
        </div>

        <nav style={styles.nav}>
          <a href="#about" style={styles.navLink}>
            About
          </a>
          <a href="#services" style={styles.navLink}>
            Services
          </a>
          <a href="#team" style={styles.navLink}>
            Team
          </a>
          <a href="#contact" style={styles.navLink}>
            Contact
          </a>
          <button style={styles.loginBtn} onClick={() => setDrawerOpen(true)}>
            Login
            <IconArrow size={16} color="#fff" />
          </button>
        </nav>
      </header>

      {/* ===== Hero ===== */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <span style={styles.heroBadge}>
            <IconBolt size={14} color="#21808D" />
            Powering Digital Commerce
          </span>
          <h1 style={styles.heroTitle}>
            Welcome to <span style={styles.heroAccent}>Zetuka Tech</span>
            <br />
            Private Limited
          </h1>
          <p style={styles.heroText}>
            We build ecommerce, quick commerce, hyperlocal delivery, and taxi
            booking platforms — plus custom software development to power
            modern businesses end to end.
          </p>
          <div style={styles.heroActions}>
            <button
              style={styles.heroPrimary}
              onClick={() => setDrawerOpen(true)}
            >
              Login to Dashboard
            </button>
            <a href="#services" style={styles.heroSecondary}>
              Explore Services
            </a>
          </div>
        </div>
      </section>

      {/* ===== Services ===== */}
      <section id="services" style={styles.section}>
        <h2 style={styles.sectionTitle}>What We Do</h2>
        <p style={styles.sectionLead}>
          End-to-end technology for commerce, logistics, mobility, and software.
        </p>
        <div style={styles.cards}>
          {features.map((f) => (
            <div key={f.title} style={styles.card}>
              <div style={styles.cardIcon}>{f.icon}</div>
              <h3 style={styles.cardTitle}>{f.title}</h3>
              <p style={styles.cardText}>{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== About ===== */}
      <section id="about" style={styles.about}>
        <h2 style={styles.sectionTitle}>About Zetuka Tech</h2>
        <p style={styles.aboutText}>
          Zetuka Tech Private Limited is a technology company focused on building
          modern digital infrastructure for businesses. From ecommerce and
          quick commerce to hyperlocal delivery, taxi booking, and custom
          software development, we help partners run their operations
          efficiently and grow with confidence.
        </p>
        <div style={styles.stats}>
          {stats.map((s) => (
            <div key={s.label} style={styles.stat}>
              <div style={styles.statValue}>{s.value}</div>
              <div style={styles.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Leadership ===== */}
      <section id="team" style={styles.section}>
        <h2 style={styles.sectionTitle}>Our Leadership</h2>
        <p style={styles.sectionLead}>
          The people driving Zetuka Tech Private Limited forward.
        </p>
        <div style={styles.teamGrid}>
          {team.map((m) => (
            <div key={m.name} style={styles.teamCard}>
              <div style={styles.avatar}>{m.initials}</div>
              <h3 style={styles.teamName}>{m.name}</h3>
              <p style={styles.teamRole}>{m.role}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer id="contact" style={styles.footer}>
        <div style={styles.footerBrand}>ZETUKA TECH PRIVATE LIMITED</div>
        <a href="mailto:info@zapee.in" style={styles.footerEmail}>
          info@zapee.in
        </a>
        <div style={styles.footerLinks}>
          <Link to="/privacy-policy" style={styles.footerLink}>
            Privacy Policy
          </Link>
          <Link to="/terms-conditions" style={styles.footerLink}>
            Terms &amp; Conditions
          </Link>
          <Link to="/refund-policy" style={styles.footerLink}>
            Refund Policy
          </Link>
        </div>
        <p style={styles.footerText}>
          © {new Date().getFullYear()} Zetuka Tech Private Limited. All rights
          reserved.
        </p>
      </footer>

      {/* ===== Fullscreen Login Overlay ===== */}
      {drawerOpen && (
        <LoginFullscreen onClose={() => setDrawerOpen(false)} />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

/* ---------- Login Screen (MongoDB-inspired) ---------- */
const LoginFullscreen: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [email, setEmail]             = useState<string>("");
  const [password, setPassword]       = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]             = useState<string>("");
  const [loading, setLoading]         = useState<boolean>(false);
  const navigate = useNavigate();

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const uid = credential.user.uid;
      const shareSnap = await getDoc(doc(db, "zapee_share_holders", uid));
      if (!shareSnap.exists()) {
        await signOut(auth);
        setError("Access denied: No shareholder account found for this email.");
        setLoading(false);
        return;
      }
      navigate("/admin");
    } catch (err: any) {
      setError(err.message || "Failed to login");
    } finally {
      setLoading(false);
    }
  };

  const focusInput = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = "#29A05A";
    e.target.style.boxShadow   = "0 0 0 3px rgba(41,160,90,0.2)";
  };
  const blurInput = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = "rgba(255,255,255,0.12)";
    e.target.style.boxShadow   = "none";
  };

  return (
    <div style={ls.wrap}>
      {/* ── Left dark panel ── */}
      <div style={ls.left}>
        {/* subtle radial glow */}
        <div style={ls.leftGlow} />

        <div style={ls.leftInner}>
          {/* Brand */}
          <div style={ls.brand}>
            <div style={ls.brandLogoWrap}>
              <img src="/logoZapee.jpeg" alt="Zapee" style={ls.brandLogoImg} />
            </div>
            <div>
              <div style={ls.brandName}>ZAPEE</div>
              <div style={ls.brandSub}>by Zetuka Tech</div>
            </div>
          </div>

          {/* Headline */}
          <h1 style={ls.headline}>
            Your delivery<br />empire, unified.
          </h1>
          <p style={ls.subtext}>
            Manage orders, riders, restaurants and analytics — all from one powerful dashboard.
          </p>

          {/* Feature list */}
          <div style={ls.featureList}>
            {[
              'Live order tracking & assignment',
              'Real-time revenue analytics',
              'Rider & restaurant management',
              'Payroll, settlement & reports',
            ].map(label => (
              <div key={label} style={ls.featureRow}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                  <circle cx="8" cy="8" r="7" stroke="#4ECDC4" strokeWidth="1.5"/>
                  <polyline points="5,8.5 7,10.5 11,6.5" stroke="#4ECDC4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span style={ls.featureLabel}>{label}</span>
              </div>
            ))}
          </div>

          {/* Divider + version tag */}
          <div style={ls.badge}>
            <span style={ls.badgeDot} />
            Admin Panel v3 · Zapee
          </div>
        </div>

        <div style={ls.leftFooter}>
          Crafted with <span style={{ color: '#E5484D' }}>♥</span> by{' '}
          <strong style={{ color: 'rgba(255,255,255,0.85)' }}>Muhammed A Awal</strong>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div style={ls.right}>
        <div style={ls.card}>
          {/* Card header */}
          <div style={ls.cardHeader}>
            <div style={ls.cardLogoWrap}>
              <img src="/logoZapee.jpeg" alt="Zapee" style={ls.cardLogoImg} />
            </div>
            <h2 style={ls.cardTitle}>Sign in to Zapee</h2>
            <p style={ls.cardSub}>Enter your credentials to continue</p>
          </div>

          {/* Divider */}
          <div style={ls.divider} />

          <form onSubmit={handleLogin} style={ls.form}>
            {/* Email */}
            <div style={ls.field}>
              <label style={ls.fieldLabel}>Email address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@zapee.com"
                required
                autoComplete="email"
                style={ls.input}
                onFocus={focusInput}
                onBlur={blurInput}
              />
            </div>

            {/* Password */}
            <div style={ls.field}>
              <label style={ls.fieldLabel}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  style={{ ...ls.input, paddingRight: 42 }}
                  onFocus={focusInput}
                  onBlur={blurInput}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={ls.eyeBtn}
                  tabIndex={-1}
                >
                  {showPassword
                    ? <IconEyeOff size={16} color="#64748b" />
                    : <IconEye   size={16} color="#64748b" />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={ls.errorBox}>
                <IconAlert size={15} color="#dc2626" strokeWidth={2} />
                <span style={ls.errorText}>{error}</span>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={loading ? { ...ls.submitBtn, opacity: 0.72, cursor: 'not-allowed' } : ls.submitBtn}
            >
              {loading
                ? <><IconSpinner size={16} />&nbsp;&nbsp;Signing in…</>
                : 'Sign in'}
            </button>
          </form>

          <p style={ls.cardFooter}>
            Zetuka Tech Admin Panel &nbsp;·&nbsp; Secure Access
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes loginFade {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

const features = [
  {
    icon: <IconCart size={24} color="#21808D" />,
    title: "Ecommerce",
    text: "Full-featured online stores with catalogs, carts, and secure checkout.",
  },
  {
    icon: <IconBolt size={24} color="#21808D" />,
    title: "Quick Commerce",
    text: "Ultra-fast delivery of everyday essentials in just minutes.",
  },
  {
    icon: <IconPin size={24} color="#21808D" />,
    title: "Hyperlocal Delivery",
    text: "Connecting local stores and customers with on-demand logistics.",
  },
  {
    icon: <IconCar size={24} color="#21808D" />,
    title: "Taxi Booking",
    text: "Ride-hailing platforms with live tracking and fare management.",
  },
  {
    icon: <IconCode size={24} color="#21808D" />,
    title: "Software Development",
    text: "Custom web, mobile, and cloud software built to scale.",
  },
];

const team = [
  { name: "Abdul Awal", role: "Founder", initials: "AA" },
  { name: "Hafizul Islam", role: "Chief Executive Officer", initials: "HI" },
  {
    name: "Advocate Nehrur Islam",
    role: "Managing Director",
    initials: "NI",
  },
  { name: "Shoriful Islam", role: "Director", initials: "SI" },
];

const stats = [
  { value: "10K+", label: "Orders Processed" },
  { value: "500+", label: "Partners" },
  { value: "99.9%", label: "Uptime" },
  { value: "24/7", label: "Support" },
];

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: "100vh",
    width: "100%",
    margin: 0,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: "#1a2332",
    backgroundColor: "#ffffff",
  },

  /* Header */
  header: {
    position: "sticky",
    top: 0,
    zIndex: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 32px",
    backgroundColor: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(10px)",
    borderBottom: "1px solid #e2e8f0",
  },
  brand: { display: "flex", alignItems: "center", gap: 12 },
  brandLogo: {
    width: 40,
    height: 40,
    borderRadius: 8,
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    flexShrink: 0,
  },
  brandLogoImg: { width: "100%", height: "100%", objectFit: "cover" },
  brandName: {
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 1,
    color: "#1a2332",
  },
  brandSub: { fontSize: 11, color: "#64748b", letterSpacing: 1 },
  nav: { display: "flex", alignItems: "center", gap: 24 },
  navLink: {
    fontSize: 14,
    fontWeight: 600,
    color: "#475569",
    textDecoration: "none",
  },
  loginBtn: {
    height: 40,
    padding: "0 18px",
    backgroundColor: "#21808D",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  /* Hero */
  hero: {
    backgroundColor: "#ffffff",
    color: "#1a2332",
    padding: "80px 32px",
    display: "flex",
    justifyContent: "center",
    borderBottom: "1px solid #e2e8f0",
  },
  heroContent: { maxWidth: 760, textAlign: "center" },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 14px",
    borderRadius: 6,
    backgroundColor: "#e6f2f3",
    color: "#21808D",
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 22,
  },
  heroTitle: {
    fontSize: 42,
    fontWeight: 700,
    lineHeight: 1.2,
    margin: "0 0 20px 0",
    color: "#1a2332",
  },
  heroAccent: { color: "#21808D" },
  heroText: {
    fontSize: 17,
    lineHeight: 1.6,
    color: "#64748b",
    margin: "0 0 32px 0",
  },
  heroActions: {
    display: "flex",
    gap: 14,
    justifyContent: "center",
    flexWrap: "wrap",
  },
  heroPrimary: {
    height: 48,
    padding: "0 26px",
    backgroundColor: "#21808D",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  heroSecondary: {
    height: 48,
    padding: "0 26px",
    display: "flex",
    alignItems: "center",
    color: "#1a2332",
    border: "1px solid #cbd5e1",
    borderRadius: 6,
    fontSize: 15,
    fontWeight: 600,
    textDecoration: "none",
  },

  /* Sections */
  section: { padding: "72px 32px", maxWidth: 1100, margin: "0 auto" },
  sectionTitle: {
    fontSize: 28,
    fontWeight: 700,
    textAlign: "center",
    margin: "0 0 10px 0",
    color: "#1a2332",
  },
  sectionLead: {
    fontSize: 15,
    color: "#64748b",
    textAlign: "center",
    margin: "0 0 44px 0",
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: 20,
  },
  card: {
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "26px 22px",
    textAlign: "center",
  },
  cardIcon: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: "#e6f2f3",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
  },
  cardTitle: { fontSize: 16, fontWeight: 700, margin: "0 0 8px 0" },
  cardText: { fontSize: 14, color: "#64748b", lineHeight: 1.55, margin: 0 },

  /* Team */
  teamGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: 20,
  },
  teamCard: {
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "30px 22px",
    textAlign: "center",
  },
  avatar: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    backgroundColor: "#21808D",
    color: "#fff",
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 14px",
  },
  teamName: { fontSize: 16, fontWeight: 700, margin: "0 0 4px 0" },
  teamRole: { fontSize: 13, color: "#21808D", fontWeight: 600, margin: 0 },

  /* About */
  about: {
    backgroundColor: "#f8fafc",
    padding: "72px 32px",
    textAlign: "center",
    borderTop: "1px solid #e2e8f0",
    borderBottom: "1px solid #e2e8f0",
  },
  aboutText: {
    maxWidth: 720,
    margin: "0 auto 44px",
    fontSize: 16,
    lineHeight: 1.7,
    color: "#475569",
  },
  stats: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: 48,
  },
  stat: { textAlign: "center" },
  statValue: {
    fontSize: 30,
    fontWeight: 700,
    color: "#21808D",
  },
  statLabel: { fontSize: 13, color: "#64748b", marginTop: 4 },

  /* Footer */
  footer: {
    backgroundColor: "#1a2332",
    color: "#fff",
    textAlign: "center",
    padding: "36px 32px",
  },
  footerBrand: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 1,
    marginBottom: 8,
  },
  footerEmail: {
    display: "inline-block",
    fontSize: 13,
    color: "#7dd3c8",
    textDecoration: "none",
    fontWeight: 600,
    marginBottom: 14,
  },
  footerLinks: {
    display: "flex",
    justifyContent: "center",
    gap: 28,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  footerLink: {
    fontSize: 13,
    color: "rgba(255,255,255,0.85)",
    textDecoration: "none",
    fontWeight: 600,
  },
  footerText: { fontSize: 13, color: "rgba(255,255,255,0.6)", margin: 0 },

};

/* ── Login styles (MongoDB-inspired) ────────────────────────────────── */
const ls: { [key: string]: React.CSSProperties } = {
  wrap: {
    position: 'fixed', inset: 0, display: 'flex',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, Roboto, sans-serif",
  },

  /* Left dark panel */
  left: {
    width: '44%', minWidth: 320,
    background: 'linear-gradient(155deg, #036128 0%, #047E33 55%, #29A05A 100%)',
    display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
  },
  leftGlow: {
    position: 'absolute', top: '-10%', right: '-10%',
    width: '60%', height: '60%', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  leftInner: {
    flex: 1, padding: '52px 48px', display: 'flex',
    flexDirection: 'column', justifyContent: 'center', position: 'relative', zIndex: 1,
  },
  brand: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 56 },
  brandLogoWrap: {
    width: 44, height: 44, borderRadius: 11, overflow: 'hidden', flexShrink: 0,
    border: '1.5px solid rgba(255,255,255,0.35)',
    boxShadow: '0 0 0 3px rgba(255,255,255,0.08)',
  },
  brandLogoImg: { width: '100%', height: '100%', objectFit: 'cover' },
  brandName: { fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: 3.5 },
  brandSub: { fontSize: 10.5, color: 'rgba(255,255,255,0.7)', fontWeight: 600, letterSpacing: 0.8, marginTop: 2 },

  headline: {
    fontSize: 34, fontWeight: 700, color: '#ffffff', lineHeight: 1.22,
    margin: '0 0 16px 0', letterSpacing: -0.6,
  },
  subtext: { fontSize: 14.5, color: 'rgba(255,255,255,0.72)', lineHeight: 1.65, margin: '0 0 40px 0' },

  featureList: { display: 'flex', flexDirection: 'column', gap: 13 },
  featureRow: { display: 'flex', alignItems: 'center', gap: 11 },
  featureLabel: { fontSize: 13.5, color: 'rgba(255,255,255,0.78)', fontWeight: 500 },

  badge: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    marginTop: 48, fontSize: 11.5, color: 'rgba(255,255,255,0.45)', fontWeight: 600, letterSpacing: 0.3,
  },
  badgeDot: {
    width: 7, height: 7, borderRadius: '50%',
    backgroundColor: '#A8F0B8', boxShadow: '0 0 6px rgba(168,240,184,0.5)', flexShrink: 0,
  },

  leftFooter: {
    padding: '15px 48px',
    borderTop: '1px solid rgba(255,255,255,0.15)',
    fontSize: 12, color: 'rgba(255,255,255,0.45)',
    position: 'relative', zIndex: 1,
  },

  /* Right panel */
  right: {
    flex: 1, backgroundColor: '#0E1512',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '32px 24px', overflowY: 'auto',
  },

  /* Card */
  card: {
    width: '100%', maxWidth: 388,
    backgroundColor: '#17201B',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.09)',
    boxShadow: '0 4px 6px rgba(0,0,0,0.2), 0 16px 40px rgba(0,0,0,0.3)',
    animation: 'loginFade 0.25s ease',
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '32px 32px 24px',
    textAlign: 'center',
  },
  cardLogoWrap: {
    width: 58, height: 58, borderRadius: 14, overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.12)', margin: '0 auto 18px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
  cardLogoImg: { width: '100%', height: '100%', objectFit: 'cover' },
  cardTitle: { fontSize: 21, fontWeight: 700, color: '#E8F0E9', margin: '0 0 6px 0', letterSpacing: -0.3 },
  cardSub: { fontSize: 13.5, color: '#7A9A80', margin: 0 },

  divider: { height: 1, backgroundColor: 'rgba(255,255,255,0.07)', margin: '0 0 4px' },

  form: { padding: '20px 32px 0', display: 'flex', flexDirection: 'column', gap: 18 },

  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  fieldLabel: { fontSize: 13, fontWeight: 600, color: '#B8D4BE' },
  input: {
    width: '100%', height: 38, padding: '0 12px',
    borderRadius: 6, border: '1px solid rgba(255,255,255,0.12)',
    fontSize: 14, color: '#E8F0E9', backgroundColor: '#0E1512',
    boxSizing: 'border-box', outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  },
  eyeBtn: {
    position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer',
    padding: '4px', display: 'flex', alignItems: 'center',
  },

  errorBox: {
    display: 'flex', alignItems: 'flex-start', gap: 8,
    backgroundColor: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.3)',
    borderRadius: 6, padding: '10px 12px',
  },
  errorText: { fontSize: 13, color: '#f87171', lineHeight: 1.45, margin: 0 },

  submitBtn: {
    height: 38, backgroundColor: '#047E33', color: '#ffffff',
    border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600,
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 8, letterSpacing: 0.1, marginTop: 2,
    transition: 'background-color 0.15s ease',
  },

  cardFooter: {
    textAlign: 'center', fontSize: 12, color: '#4A6A50',
    padding: '20px 32px 26px', margin: 0,
  },
};

const LoginScreen: React.FC = () => <LoginFullscreen onClose={() => {}} />;
export default LoginScreen;
